import { c as _c } from "react/compiler-runtime";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { TilesContainer } from "@components/common/tiles-container";
import { IntegrationsInProgressDialog } from "@components/integrations-dialog/integrations-in-progress-dialog";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useTokensService } from "@context-providers/tokens-service-provider";
import { IntegrationUserConnectionOrigin } from "@custom-types/integrations-types";
import { useToast } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { IntegrationTile } from "@pages/project-details/project-integrations/integration-tile";
import { ProjectIntegrationsConnectDialog } from "@pages/project-details/project-integrations/project-integrations-connect-dialog";
import { ProjectIntegrationsConnectedDialog } from "@pages/project-details/project-integrations/project-integrations-connected-dialog";
import { ProjectIntegrationsDisconnectDialog } from "@pages/project-details/project-integrations/project-integrations-disconnect-dialog";
import { IntegrationsService } from "@services/integrations-service/integrations-service";
import { REAUTHORIZATION_NOT_COMPLETED } from "@services/integrations-service/integrations-service-constants";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { FaroCoreApiConst } from "@stellar/core-api-client";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo, useState } from "react";

/** Renders project integration page */
export function ProjectIntegrations(t0) {
  const $ = _c(62);
  const {
    projectId
  } = t0;
  const {
    handleErrorWithPage,
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  const {
    createDialog
  } = useSphereDialog();
  const {
    trackEvent
  } = useTrackEvent();
  const tokensService = useTokensService();
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const projectApiClient = useProjectApiClient(t1);
  const [integrationId, setIntegrationId] = useState("autodesk");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [integrationTokens, setIntegrationTokens] = useState(t2);
  const [projectIntegrations, setProjectIntegrations] = useState(null);
  const [dialogState, setDialogState] = useState("none");
  let t3;
  let t4;
  if ($[3] !== integrationTokens) {
    const userIntegrations = integrationTokens.map(_temp);
    t3 = userIntegrations.includes("autodesk");
    t4 = userIntegrations.includes("procore");
    $[3] = integrationTokens;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      "autodesk": t3,
      "procore": t4
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const userConnections = t5;
  let t6;
  if ($[9] !== createDialog || $[10] !== openToast || $[11] !== tokensService) {
    t6 = async integrationId_0 => {
      try {
        await createDialog({
          title: "Re-authorization required",
          isHideCancel: true,
          isHideClose: true
        }, `You need to authorize ${INTEGRATION_INFO[integrationId_0].displayName} again`);
        setDialogState("userConnection");
        const updatedTokens = await tokensService.authorizeIntegration(integrationId_0);
        setIntegrationTokens(updatedTokens);
        setDialogState("none");
        openToast({
          title: "Integration successfully re-authorized"
        });
      } catch {
        setDialogState("none");
        throw new Error(REAUTHORIZATION_NOT_COMPLETED);
      }
    };
    $[9] = createDialog;
    $[10] = openToast;
    $[11] = tokensService;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const reauthorize = t6;
  let t7;
  if ($[13] !== projectApiClient || $[14] !== reauthorize) {
    t7 = new IntegrationsService({
      projectApiClient,
      reauthorize
    });
    $[13] = projectApiClient;
    $[14] = reauthorize;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const integrationsService = t7;
  let t8;
  if ($[16] !== openToast || $[17] !== tokensService) {
    t8 = () => {
      tokensService.closeAuthorizationWindow();
      setDialogState("none");
      openToast({
        title: "Couldn't enable the integration",
        message: "The reauthorization was canceled.",
        variant: "warning"
      });
    };
    $[16] = openToast;
    $[17] = tokensService;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const cancelUserConnection = t8;
  let t9;
  if ($[19] !== handleErrorWithToast || $[20] !== openToast || $[21] !== tokensService || $[22] !== trackEvent) {
    t9 = async integrationId_1 => {
      trackEvent({
        name: UserProfile.connectIntegration,
        props: {
          integrationId: integrationId_1,
          from: IntegrationUserConnectionOrigin.projectIntegrations
        }
      });
      setDialogState("userConnection");
      ;
      try {
        const updatedTokens_0 = await tokensService.authorizeIntegration(integrationId_1);
        setIntegrationTokens(updatedTokens_0);
        openToast({
          title: "Integration connected"
        });
        return true;
      } catch (t10) {
        const error = t10;
        handleErrorWithToast({
          id: `connectIntegration-${Date.now().toString()}`,
          title: "Failed to connect to the integration.",
          error
        });
        return false;
      }
    };
    $[19] = handleErrorWithToast;
    $[20] = openToast;
    $[21] = tokensService;
    $[22] = trackEvent;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const connectAtUserLevel = t9;
  let t10;
  if ($[24] !== connectAtUserLevel || $[25] !== integrationsService || $[26] !== projectIntegrations || $[27] !== userConnections) {
    t10 = async integrationId_2 => {
      setIntegrationId(integrationId_2);
      const isConnectedAtProjectLevel = !!projectIntegrations && integrationsService.isConnected(integrationId_2, projectIntegrations);
      const isConnectedAtUserLevel = userConnections[integrationId_2];
      if (isConnectedAtProjectLevel) {
        setDialogState("disconnect");
      }
      if (!isConnectedAtProjectLevel && !isConnectedAtUserLevel) {
        const isSuccess = await connectAtUserLevel(integrationId_2);
        setDialogState(isSuccess ? "connect" : "none");
      }
      if (!isConnectedAtProjectLevel && isConnectedAtUserLevel) {
        setDialogState("connect");
      }
    };
    $[24] = connectAtUserLevel;
    $[25] = integrationsService;
    $[26] = projectIntegrations;
    $[27] = userConnections;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const onActionButtonClick = t10;
  let t11;
  let t12;
  if ($[29] !== handleErrorWithPage || $[30] !== integrationsService || $[31] !== tokensService) {
    t11 = () => {
      const getData = async function getData() {
        ;
        try {
          const [projectIntegrations_0, integrationTokens_0] = await Promise.all([integrationsService.getProjectIntegrations(), tokensService.getIntegrationTokens()]);
          setProjectIntegrations(projectIntegrations_0);
          setIntegrationTokens(integrationTokens_0);
        } catch (t13) {
          const error_0 = t13;
          handleErrorWithPage({
            id: `getProjectIntegrationsData-${Date.now().toString()}`,
            title: "Failed to load the project integrations data. Please refresh the page and try again.",
            error: error_0
          });
        }
      };
      getData();
    };
    t12 = [handleErrorWithPage, integrationsService, tokensService];
    $[29] = handleErrorWithPage;
    $[30] = integrationsService;
    $[31] = tokensService;
    $[32] = t11;
    $[33] = t12;
  } else {
    t11 = $[32];
    t12 = $[33];
  }
  useEffect(t11, t12);
  let t13;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      my: "25px"
    };
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== integrationsService || $[36] !== onActionButtonClick || $[37] !== projectIntegrations) {
    t14 = FaroCoreApiConst.allIntegrationIds.map(id => <IntegrationTile key={id} integrationId={id} onActionButtonClick={onActionButtonClick} integrationsService={integrationsService} setProjectIntegrations={setProjectIntegrations} projectIntegrations={projectIntegrations} />);
    $[35] = integrationsService;
    $[36] = onActionButtonClick;
    $[37] = projectIntegrations;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t14) {
    t15 = <TilesContainer>{t14}</TilesContainer>;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== dialogState || $[42] !== integrationId) {
    t16 = dialogState === "connected" && <ProjectIntegrationsConnectedDialog integrationId={integrationId} onClose={() => setDialogState("none")} />;
    $[41] = dialogState;
    $[42] = integrationId;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== dialogState || $[45] !== integrationId || $[46] !== integrationsService) {
    t17 = dialogState === "connect" && <ProjectIntegrationsConnectDialog integrationsService={integrationsService} integrationId={integrationId} setProjectIntegrations={setProjectIntegrations} setDialogState={setDialogState} />;
    $[44] = dialogState;
    $[45] = integrationId;
    $[46] = integrationsService;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== dialogState || $[49] !== integrationId || $[50] !== integrationsService) {
    t18 = dialogState === "disconnect" && <ProjectIntegrationsDisconnectDialog integrationsService={integrationsService} integrationId={integrationId} setProjectIntegrations={setProjectIntegrations} onClose={() => setDialogState("none")} />;
    $[48] = dialogState;
    $[49] = integrationId;
    $[50] = integrationsService;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== cancelUserConnection || $[53] !== dialogState || $[54] !== integrationId) {
    t19 = dialogState === "userConnection" && <IntegrationsInProgressDialog integrationId={integrationId} dialogCancelCallback={cancelUserConnection} />;
    $[52] = cancelUserConnection;
    $[53] = dialogState;
    $[54] = integrationId;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t15 || $[57] !== t16 || $[58] !== t17 || $[59] !== t18 || $[60] !== t19) {
    t20 = <Box component="div" sx={t13}>{t15}{t16}{t17}{t18}{t19}</Box>;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  return t20;
}
function _temp(token) {
  return token.provider;
}